/**
 * Phase 4 Item 3 — data hygiene utilities.
 *
 * Pure functions. No React, no side effects. Safe to import anywhere.
 *
 * - titleCaseName(s)  → normalizes person-name casing with Mc/Mac,
 *                        apostrophe, hyphen, and trailing Roman numeral
 *                        handling. Fires on blur from input onBlur
 *                        handlers; intentionally NOT on keystroke
 *                        (would feel disorienting).
 * - isValidEmail(s)   → reasonable (non-RFC-5322-strict) validator for
 *                        user-facing "does this look like an email?"
 *                        prompts. Does NOT check deliverability.
 * - formatPhone(s)    → normalize US phone to `(123) 456-7890` for
 *                        display and digits-only for storage. Accepts
 *                        10-digit or 11-digit (strips leading 1).
 *
 * v1 limitations (all accepted — documented here so future readers
 * don't chase "bugs" that are intentional):
 *   - Particles like "van der", "de la" get title-cased.
 *   - Intentional lowercase names ("bell hooks", "danah boyd") get
 *     title-cased.
 *   - Only US 10-digit phone is supported. International numbers are
 *     rejected as "invalid" (acceptable for worksite-US product).
 */

const ROMAN_NUMERALS = new Set([
    "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X",
    "XI", "XII",   // edge cases, rarely a suffix but cheap to include
]);

// ---------------------------------------------------------------- Title-case

function titleCaseWord(raw) {
    if (!raw) return raw;

    // Trailing Roman numerals like "III" → uppercase
    if (ROMAN_NUMERALS.has(raw.toUpperCase())) {
        return raw.toUpperCase();
    }

    // Hyphenated segments ("smith-jones" → "Smith-Jones")
    if (raw.includes("-")) {
        return raw.split("-").map(titleCaseWord).join("-");
    }

    // Apostrophe segments ("o'brien" → "O'Brien", "d'angelo" → "D'Angelo")
    if (raw.includes("'") || raw.includes("\u2019")) {
        const sep = raw.includes("\u2019") ? "\u2019" : "'";
        return raw.split(sep).map(titleCaseWord).join(sep);
    }

    const lower = raw.toLowerCase();

    // Mc / Mac prefixes — "mcdonald" → "McDonald", "macleod" → "MacLeod"
    // Guard Mac: only apply when the rest starts with a consonant cluster
    // we expect to be capitalised. "mack" → "Mack" (not "MacK"),
    // "maceachern" → "MacEachern". Simplest heuristic: require 5+ chars.
    if (lower.startsWith("mc") && lower.length >= 4) {
        return "Mc" + lower.charAt(2).toUpperCase() + lower.slice(3);
    }
    if (lower.startsWith("mac") && lower.length >= 5) {
        return "Mac" + lower.charAt(3).toUpperCase() + lower.slice(4);
    }

    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function titleCaseName(s) {
    if (s == null) return "";
    const trimmed = String(s).trim().replace(/\s+/g, " ");
    if (!trimmed) return "";
    return trimmed.split(" ").map(titleCaseWord).join(" ");
}

// -------------------------------------------------------------------- Email

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmail(s) {
    if (s == null) return false;
    return EMAIL_RE.test(String(s).trim());
}

// -------------------------------------------------------------------- Phone

/**
 * Returns { digits, display, valid }.
 *   digits  — digits-only string (10 characters on valid, otherwise what
 *             the user typed minus non-digits, for round-tripping).
 *   display — "(123) 456-7890" on valid, original string on invalid.
 *   valid   — boolean.
 *
 * Accepts 10-digit strictly, or 11-digit when leading "1" (country code).
 * Rejects everything else.
 */
export function formatPhone(s) {
    const raw = s == null ? "" : String(s);
    const digits = raw.replace(/\D+/g, "");
    let canonical = digits;
    if (digits.length === 11 && digits.startsWith("1")) {
        canonical = digits.slice(1);
    }
    if (canonical.length !== 10) {
        return { digits: canonical, display: raw, valid: false };
    }
    const display = `(${canonical.slice(0, 3)}) ${canonical.slice(3, 6)}-${canonical.slice(6)}`;
    return { digits: canonical, display, valid: true };
}
